import { Result, Subtask } from 'constants/interfaces'
import keys from 'constants/keys'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'

dayjs.extend(customParseFormat)
dayjs.extend(utc)

export const parseRecurrenceTimestamp = (recurrenceTs: string) => {
  const parsedRecurrence = dayjs.utc(recurrenceTs, 'YYYYMMDDHHmmss', true)
  return parsedRecurrence.isValid() ? parsedRecurrence.unix() : null
}

export const passesRecurrenceFilter = (result: Result, recurrenceTs?: string | null) => {
  if (!recurrenceTs) return true

  if (result.context_id_recurrence_ts) {
    return result.context_id_recurrence_ts === `${result.context_id}_${recurrenceTs}`
  }

  if (!result.updated_at_ts) return false

  const recurrenceUnix = parseRecurrenceTimestamp(recurrenceTs)
  return recurrenceUnix === null ? true : Number(result.updated_at_ts) >= recurrenceUnix
}

/** A rejected submission remains completed for review, but can be reworked by its executor. */
export const isResultCompleted = (result?: Result) =>
  !!result &&
  [
    keys.TASK_STATUSES.DONE.value,
    keys.TASK_STATUSES.SKIPPED.value,
    keys.TASK_STATUSES.APPROVED.value,
    keys.TASK_STATUSES.APPROVED_WITH_RESERVATION.value,
    keys.TASK_STATUSES.DISAPPROVED.value,
    keys.TASK_STATUSES.REVIEWED_BY_AI.value,
    -1, // Historical completed results.
  ].includes(result.status)

export const isResultReviewable = (result?: Result) =>
  !!result && isResultCompleted(result) && result.status !== keys.TASK_STATUSES.SKIPPED.value && result.status !== -1

/** Never fall back to another occurrence. Prefer the newest matching server row. */
export const findScopedResult = (
  results: Result[] = [],
  stId: string,
  contextId: string,
  recurrenceTs?: string | null
): Result | undefined =>
  results.reduce<Result | undefined>((latest, result) => {
    if (result.st_id !== stId || result.context_id !== contextId || !passesRecurrenceFilter(result, recurrenceTs))
      return latest
    return !latest || Number(result.updated_at_ts || 0) >= Number(latest.updated_at_ts || 0) ? result : latest
  }, undefined)

/** Overview/export rows use the same occurrence and latest-response rule as individual execution. */
export const findLatestSubtaskResults = (results: Result[] = [], stId: string, recurrenceTs?: string | null) => {
  const byContext = new Map<string, Result>()
  results.forEach((result) => {
    if (!result || result.st_id !== stId || !passesRecurrenceFilter(result, recurrenceTs)) return
    const previous = byContext.get(result.context_id)
    if (!previous || Number(result.updated_at_ts || 0) >= Number(previous.updated_at_ts || 0)) {
      byContext.set(result.context_id, result)
    }
  })
  return Array.from(byContext.values())
}

export const areSubtasksCompleted = (
  subtasks: Subtask[],
  results: Result[],
  contextId: string,
  recurrenceTs?: string | null
) =>
  subtasks.length > 0 &&
  subtasks.every((subtask) => isResultCompleted(findScopedResult(results, subtask.st_id, contextId, recurrenceTs)))
